import { HttpErrorResponse, HttpEventType, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { tap } from 'rxjs/operators';

import { AnalyticsEvent } from '../../../../analytics-events';
import { environment } from '../../../environments/environment';
import { TelemetryService } from './telemetry.service';

/**
 * MOB-100 Stage 28 — HTTP telemetry interceptor.
 *
 * Captures per-request latency + outcome for every `apiUrl`-targeted call
 * the mobile app makes. Mirrors the BRD §10 NFR "API p50/p95 latency"
 * performance metric the prompt calls out.
 *
 * What is reported:
 *   - `api.request_completed` on 2xx/3xx with `route`, `method`, `status`,
 *     `durationMs`.
 *   - `api.request_failed` on 4xx/5xx OR network errors with `route`,
 *     `method`, `status`, `errorCode`.
 *
 * The route argument is the STATIC route template (e.g.
 * `/api/mobile/booth/{id}/profile`) — concrete numeric ids are
 * substituted with `{id}` so PostHog / Mixpanel cardinality stays
 * bounded AND so the route does not become a covert PII channel.
 *
 * Order in `withInterceptors([...])`: `telemetryInterceptor` runs LAST so
 * the timer is started after `authInterceptor` attaches the bearer (its
 * latency budget is part of the request budget). See `app.config.ts`.
 */
export const telemetryInterceptor: HttpInterceptorFn = (req, next) => {
  // Only instrument calls to our own backend — third-party requests (CDN,
  // analytics SDKs themselves, blob storage signed urls) are noise.
  if (!isAppApiUrl(req.url)) {
    return next(req);
  }
  const telemetry = inject(TelemetryService);
  const startedAt = Date.now();
  const route = normaliseRoute(req.url);
  const method = (req.method || 'GET').toUpperCase();

  return next(req).pipe(
    tap({
      next: (event) => {
        if (event.type !== HttpEventType.Response) return;
        const durationMs = Date.now() - startedAt;
        telemetry.trackEvent(AnalyticsEvent.api_request_completed, {
          route,
          method,
          status: event.status,
          durationMs
        });
      },
      error: (err: unknown) => {
        const durationMs = Date.now() - startedAt;
        const status = err instanceof HttpErrorResponse ? err.status : 0;
        const errorCode = readErrorCode(err);
        telemetry.trackEvent(AnalyticsEvent.api_request_failed, {
          route,
          method,
          status,
          errorCode,
          durationMs
        });
        // Server-side 5xx / network errors are crash-report worthy. 4xx
        // is product-domain — Sentry would just create noise.
        if (status === 0 || status >= 500) {
          telemetry.captureException(err, { route, method, status });
        }
      }
    })
  );
};

function isAppApiUrl(url: string): boolean {
  if (!url) return false;
  // Treat any /api/* on our own host as an app API; allow relative URLs.
  if (url.startsWith('/api/')) return true;
  try {
    const base = environment.apiUrl;
    return Boolean(base) && url.startsWith(base);
  } catch {
    return false;
  }
}

/**
 * Strip everything after the path and substitute every numeric segment
 * with `{id}` so route cardinality stays bounded. Examples:
 *   /api/mobile/booth/123/profile          → /api/mobile/booth/{id}/profile
 *   /api/mobile/your-events?cursor=foo     → /api/mobile/your-events
 *   /api/mobile/session/abc-uuid/transcript → /api/mobile/session/{id}/transcript
 */
function normaliseRoute(rawUrl: string): string {
  let path = rawUrl;
  try {
    if (path.startsWith('http')) {
      const u = new URL(path);
      path = u.pathname;
    } else {
      const qIndex = path.indexOf('?');
      if (qIndex >= 0) path = path.slice(0, qIndex);
    }
  } catch {
    /* keep raw */
  }
  return path
    .split('/')
    .map((segment) => {
      if (!segment) return segment;
      if (/^\d+$/.test(segment)) return '{id}';
      if (/^[0-9a-f-]{20,}$/i.test(segment)) return '{id}';
      if (/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(segment)) {
        return '{id}';
      }
      return segment;
    })
    .join('/');
}

function readErrorCode(err: unknown): string {
  if (err instanceof HttpErrorResponse) {
    const body = err.error as { errorCode?: string } | null;
    if (body && typeof body.errorCode === 'string') return body.errorCode;
    if (err.status === 0) return 'network';
    return `http-${err.status}`;
  }
  return 'unknown';
}
